import React, { useState, useContext, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import clsx from "clsx";
import {
	makeStyles,
	Drawer,
	AppBar,
	Toolbar,
	List,
	Typography,
	Divider,
	MenuItem,
	IconButton,
	Menu,
	Avatar,
	Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import Tutorial from "../components/Tutorial";
import TransferAlert from "../components/TransferAlert";
import ReplyNotificationToasts from "../components/ReplyNotificationToasts";
import InboxButton from "../components/InboxButton";
import ConnectionAlerts from "../components/ConnectionAlerts";
import ConnectionOfflineBanner from "../components/ConnectionOfflineBanner";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 250;

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		height: "100vh",
		[theme.breakpoints.down("sm")]: {
			height: "calc(100vh - 56px)",
		},
	},
	toolbar: {
		paddingRight: 24,
		[theme.breakpoints.down("xs")]: { paddingLeft: 8, paddingRight: 8, "& .MuiIconButton-root": { padding: 7 } },
	},
	brandArea: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "16px 12px 16px 20px",
		minHeight: 56,
	},
	brandAreaClosed: {
		justifyContent: "center",
		padding: "16px 8px",
	},
	brand: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	brandIcon: {
		width: 34,
		height: 34,
		borderRadius: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		flexShrink: 0,
	},
	brandName: {
		color: "#fff",
		fontWeight: 800,
		fontSize: "1.05rem",
		letterSpacing: "-0.02em",
		whiteSpace: "nowrap",
	},
	collapseButton: {
		color: theme.custom.sidebarText,
		padding: 6,
	},
	appBar: {
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		backgroundColor: theme.custom.surface,
		color: theme.palette.text.primary,
		boxShadow: "none",
		borderBottom: `1px solid ${theme.custom.border}`,
	},
	appBarShift: {
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		[theme.breakpoints.up("sm")]: {
			marginLeft: drawerWidth,
			width: `calc(100% - ${drawerWidth}px)`,
		},
	},
	// drawer recolhido (mini): a barra desloca a largura dos icones
	appBarClosed: {
		[theme.breakpoints.up("sm")]: {
			marginLeft: theme.spacing(9),
			width: `calc(100% - ${theme.spacing(9)}px)`,
		},
	},
	menuButton: {
		marginRight: 16,
		color: theme.palette.text.primary,
	},
	menuButtonHidden: {
		display: "none",
	},
	title: {
		flexGrow: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	greeting: {
		fontWeight: 700,
		fontSize: "0.95rem",
		lineHeight: 1.2,
		whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
		color: theme.palette.text.primary,
	},
	greetingSub: {
		fontSize: "0.72rem",
		[theme.breakpoints.down("xs")]: { display: "none" },
		color: theme.palette.text.secondary,
	},
	drawerPaper: {
		position: "relative",
		whiteSpace: "nowrap",
		width: drawerWidth,
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		backgroundColor: theme.custom.sidebarBg,
		color: theme.custom.sidebarText,
		borderRight: "none",
	},
	drawerPaperClose: {
		overflowX: "hidden",
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		width: theme.spacing(7),
		[theme.breakpoints.up("sm")]: {
			width: theme.spacing(9),
		},
	},
	sidebarDivider: {
		backgroundColor: "rgba(148, 163, 184, 0.15)",
		margin: "4px 12px",
	},
	navList: {
		padding: "8px 12px",
	},
	navListClosed: {
		padding: "8px 6px",
	},
	appBarSpacer: {
		minHeight: "56px",
		flexShrink: 0,
	},
	content: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		overflow: "auto",
	},
	actionIcon: {
		color: theme.palette.text.secondary,
	},
	userAvatar: {
		width: 32,
		height: 32,
		fontSize: "0.85rem",
		fontWeight: 700,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
	},
	avatarButton: {
		padding: 6,
		marginLeft: 4,
	},
}));

const LoggedInLayout = ({ children }) => {
	const classes = useStyles();
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const { handleLogout, loading } = useContext(AuthContext);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [drawerVariant, setDrawerVariant] = useState("permanent");
	const { user } = useContext(AuthContext);
	const { darkMode, toggleTheme } = useThemeContext();
	const { whatsApps } = useContext(WhatsAppsContext);
	const appBarRef = useRef(null);
	const [appBarHeight, setAppBarHeight] = useState(56);
	const measureAppBar = useCallback(() => {
		const height = appBarRef.current?.getBoundingClientRect().height;
		if (!height) return;
		const nextHeight = Math.ceil(height);
		setAppBarHeight(current => current === nextHeight ? current : nextHeight);
	}, []);

	useLayoutEffect(() => {
		const appBar = appBarRef.current;
		if (loading || !appBar) return undefined;
		measureAppBar();
		const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measureAppBar) : null;
		if (observer) observer.observe(appBar);
		window.addEventListener("resize", measureAppBar);
		appBar.addEventListener("transitionend", measureAppBar);
		return () => {
			if (observer) observer.disconnect();
			window.removeEventListener("resize", measureAppBar);
			appBar.removeEventListener("transitionend", measureAppBar);
		};
	}, [loading, measureAppBar]);

	// The explicit measurement also covers browsers without ResizeObserver.
	useLayoutEffect(() => { measureAppBar(); }, [whatsApps, user?.id, user?.profile, user?.super, loading, drawerOpen, darkMode, measureAppBar]);

	useEffect(() => {
		if (document.body.offsetWidth > 600) {
			setDrawerOpen(true);
		}
	}, []);

	useEffect(() => {
		if (document.body.offsetWidth < 600) {
			setDrawerVariant("temporary");
		} else {
			setDrawerVariant("permanent");
		}
	}, [drawerOpen]);

	const handleMenu = (event) => {
		setAnchorEl(event.currentTarget);
		setMenuOpen(true);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setMenuOpen(false);
	};

	const handleOpenUserModal = () => {
		setUserModalOpen(true);
		handleCloseMenu();
	};

	const handleClickLogout = () => {
		handleCloseMenu();
		handleLogout();
	};

	const drawerClose = () => {
		if (document.body.offsetWidth < 600) {
			setDrawerOpen(false);
		}
	};

	if (loading) {
		return <BackdropLoading />;
	}

	const userInitials = (user?.name || "?")
		.split(" ")
		.map((n) => n[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

	return (
		<div className={classes.root}>
			<Drawer
				variant={drawerVariant}
				className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
				classes={{
					paper: clsx(
						classes.drawerPaper,
						!drawerOpen && classes.drawerPaperClose
					),
				}}
				PaperProps={{ "data-tour": "sidebar" }}
				open={drawerOpen}
			>
				<div
					className={clsx(
						classes.brandArea,
						!drawerOpen && classes.brandAreaClosed
					)}
				>
					<div className={classes.brand}>
						<div className={classes.brandIcon}>
							<WhatsAppIcon fontSize="small" />
						</div>
						{drawerOpen && (
							<Typography className={classes.brandName}>Flytec</Typography>
						)}
					</div>
					{drawerOpen && (
						<IconButton
							className={classes.collapseButton}
							onClick={() => setDrawerOpen(!drawerOpen)}
						>
							<ChevronLeftIcon />
						</IconButton>
					)}
				</div>
				<Divider className={classes.sidebarDivider} />
				<List
					className={clsx(
						classes.navList,
						!drawerOpen && classes.navListClosed
					)}
				>
					<MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
				</List>
			</Drawer>
			<UserModal
				open={userModalOpen}
				onClose={() => setUserModalOpen(false)}
				userId={user?.id}
			/>
			<AppBar
				ref={appBarRef}
				position="absolute"
				className={clsx(
					classes.appBar,
					drawerOpen ? classes.appBarShift : classes.appBarClosed
				)}
			>
				<Toolbar variant="dense" className={classes.toolbar}>
					<IconButton
						edge="start"
						aria-label="open drawer"
						onClick={() => setDrawerOpen(!drawerOpen)}
						className={clsx(
							classes.menuButton,
							drawerOpen && classes.menuButtonHidden
						)}
					>
						<MenuIcon />
					</IconButton>
					<div className={classes.title}>
						<Typography className={classes.greeting}>
							Olá, {user?.name || ""}
						</Typography>
						<Typography className={classes.greetingSub}>
							{new Date().toLocaleDateString("pt-BR", {
								weekday: "long",
								day: "numeric",
								month: "long",
							})}
						</Typography>
					</div>

					<Tooltip title={darkMode ? "Modo claro" : "Modo escuro"}>
						<IconButton onClick={toggleTheme} className={classes.actionIcon}>
							{darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
						</IconButton>
					</Tooltip>

					{user.id && <ConnectionAlerts />}
					{user.id && <InboxButton />}
					{user.id && <NotificationsPopOver />}

					<div>
						<Tooltip title={user?.name || ""}>
							<IconButton
								aria-label="account of current user"
								aria-controls="menu-appbar"
								aria-haspopup="true"
								onClick={handleMenu}
								className={classes.avatarButton}
							>
								<Avatar className={classes.userAvatar}>{userInitials}</Avatar>
							</IconButton>
						</Tooltip>
						<Menu
							id="menu-appbar"
							anchorEl={anchorEl}
							getContentAnchorEl={null}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "right",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "right",
							}}
							open={menuOpen}
							onClose={handleCloseMenu}
						>
							<MenuItem onClick={handleOpenUserModal}>
								{i18n.t("mainDrawer.appBar.user.profile")}
							</MenuItem>
							<MenuItem
								onClick={() => {
									handleCloseMenu();
									window.dispatchEvent(new Event("flytec:start-tutorial"));
								}}
							>
								🎧 Refazer tutorial
							</MenuItem>
							<MenuItem onClick={handleClickLogout}>
								{i18n.t("mainDrawer.appBar.user.logout")}
							</MenuItem>
						</Menu>
					</div>
				</Toolbar>
				<ConnectionOfflineBanner />
			</AppBar>
			<main className={classes.content} data-tour="main-content">
				<div className={classes.appBarSpacer} style={{ height: appBarHeight, minHeight: appBarHeight }} aria-hidden="true" />
				{children ? children : null}
			</main>
			<Tutorial />
			<TransferAlert />
			<ReplyNotificationToasts />
		</div>
	);
};

export default LoggedInLayout;
